import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useQueries } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import {
  useGetMe,
  getListAuditLogQueryOptions,
} from "@workspace/api-client-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarDays, Hash, ListFilter, Search, X } from "lucide-react";
import {
  ActivityTable,
  KNOWN_ACTIONS,
  actionInfo,
  CATEGORY_LABEL,
  type ActivityCategory,
} from "@/components/activity-feed";

const PAGE = 100;

const ENTITY_TYPES = [
  "account",
  "inventory",
  "bank_account",
  "bank_account_mapping",
  "fve_sale_match",
];

/** Category chips shown in the filter strip, in display order. Each maps to an
 *  existing ActivityCategory and scopes the Actions popover to its action set. */
const CATEGORY_CHIPS: ActivityCategory[] = [
  "account",
  "inventory",
  "banking",
  "phone",
  "sale",
  "fve",
];

const KNOWN_CATEGORIES = new Set<string>(CATEGORY_CHIPS);

interface Filters {
  q: string;
  actorEmail: string;
  entityType: string;
  entityId: string;
  category: ActivityCategory | "";
  actions: string[];
  from: string;
  to: string;
}

const EMPTY: Filters = {
  q: "",
  actorEmail: "",
  entityType: "",
  entityId: "",
  category: "",
  actions: [],
  from: "",
  to: "",
};

function readFromUrl(search: string): Filters {
  const p = new URLSearchParams(search);
  const cat = p.get("category") ?? "";
  return {
    q: p.get("q") ?? "",
    actorEmail: p.get("actorEmail") ?? "",
    entityType: p.get("entityType") ?? "",
    entityId: p.get("entityId") ?? "",
    category: KNOWN_CATEGORIES.has(cat) ? (cat as ActivityCategory) : "",
    actions: (p.get("action") ?? "").split(",").map((s) => s.trim()).filter(Boolean),
    from: p.get("from") ?? "",
    to: p.get("to") ?? "",
  };
}

function toQueryString(f: Filters): string {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.actorEmail) p.set("actorEmail", f.actorEmail);
  if (f.entityType) p.set("entityType", f.entityType);
  if (f.entityId) p.set("entityId", f.entityId);
  if (f.category) p.set("category", f.category);
  if (f.actions.length) p.set("action", f.actions.join(","));
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  return p.toString();
}

function useDebounced<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/** Date-only strings (YYYY-MM-DD) convert to start/end-of-day ISO timestamps. */
function toIsoStart(day: string): string | undefined {
  if (!day) return undefined;
  const d = new Date(`${day}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function toIsoEnd(day: string): string | undefined {
  if (!day) return undefined;
  const d = new Date(`${day}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function dateLabel(day: string): string {
  if (!day) return "";
  const d = new Date(`${day}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function ToggleChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-7 px-2.5 rounded-md border text-xs transition-colors ${
        active
          ? "bg-primary/15 text-primary border-primary/40"
          : "bg-card border-border text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function DateChip({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`h-7 inline-flex items-center gap-1.5 rounded-md border px-2 text-xs transition-colors ${
            value
              ? "bg-primary/15 text-primary border-primary/40"
              : "bg-card border-border text-muted-foreground hover:text-foreground"
          }`}
        >
          <CalendarDays className="h-3.5 w-3.5 shrink-0" />
          <span>{value ? dateLabel(value) : placeholder}</span>
          {value && (
            <span
              role="button"
              tabIndex={-1}
              aria-label={`Clear ${placeholder}`}
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
              className="ml-0.5 inline-flex rounded hover:bg-muted/60"
            >
              <X className="h-3 w-3" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2">
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground"
        />
      </PopoverContent>
    </Popover>
  );
}

export default function Activity() {
  const { data: me } = useGetMe();
  const [, navigate] = useLocation();

  const [filters, setFilters] = useState<Filters>(() => readFromUrl(window.location.search));
  // True offset-based pagination: each loaded page is a separate query keyed by
  // its offset, and "Load more" appends another page rather than refetching a
  // single ever-growing limit.
  const [pageCount, setPageCount] = useState(1);

  // Keep the URL query string in sync so the view is shareable/bookmarkable.
  useEffect(() => {
    const qs = toQueryString(filters);
    navigate(qs ? `/activity?${qs}` : "/activity", { replace: true });
  }, [filters, navigate]);

  const debouncedQ = useDebounced(filters.q);
  const debouncedActor = useDebounced(filters.actorEmail);
  const debouncedEntityId = useDebounced(filters.entityId);

  // When no specific actions are picked but a category is active, scope the
  // query to every action in that category.
  const effectiveActions = useMemo(() => {
    if (filters.actions.length) return filters.actions;
    if (filters.category) {
      return KNOWN_ACTIONS.filter((a) => actionInfo(a).category === filters.category);
    }
    return [];
  }, [filters.actions, filters.category]);

  const baseParams = useMemo(
    () => ({
      ...(debouncedQ ? { q: debouncedQ } : {}),
      ...(debouncedActor ? { actorEmail: debouncedActor } : {}),
      ...(filters.entityType ? { entityType: filters.entityType } : {}),
      ...(debouncedEntityId ? { entityId: debouncedEntityId } : {}),
      ...(effectiveActions.length ? { action: effectiveActions.join(",") } : {}),
      ...(toIsoStart(filters.from) ? { from: toIsoStart(filters.from) } : {}),
      ...(toIsoEnd(filters.to) ? { to: toIsoEnd(filters.to) } : {}),
    }),
    [debouncedQ, debouncedActor, filters.entityType, debouncedEntityId, effectiveActions, filters.from, filters.to],
  );

  // Reset to the first page whenever the active filter set changes.
  const filterKey = JSON.stringify(baseParams);
  useEffect(() => {
    setPageCount(1);
  }, [filterKey]);

  const pageQueries = useQueries({
    queries: Array.from({ length: pageCount }, (_, i) =>
      getListAuditLogQueryOptions({ ...baseParams, limit: PAGE, offset: i * PAGE }),
    ),
  });

  const isLoading = pageQueries[0]?.isLoading ?? true;
  const isFetching = pageQueries.some((q) => q.isFetching);
  const data = useMemo(() => {
    const items = pageQueries.flatMap((q) => q.data?.items ?? []);
    const total = pageQueries[0]?.data?.total ?? 0;
    return { items, total };
  }, [pageQueries]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  // Distinct actor emails collected from the currently loaded result set.
  const actorOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of items) {
      if (e.actorEmail) set.add(e.actorEmail);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [items]);

  const actionGroups = useMemo(() => {
    const groups = new Map<ActivityCategory, string[]>();
    for (const a of KNOWN_ACTIONS) {
      const cat = actionInfo(a).category;
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat)!.push(a);
    }
    return [...groups.entries()];
  }, []);

  // The Actions popover only shows the active category's groups when one is set.
  const visibleActionGroups = useMemo(
    () => (filters.category ? actionGroups.filter(([cat]) => cat === filters.category) : actionGroups),
    [actionGroups, filters.category],
  );

  if (me && me.role !== "admin") {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full w-full">
          <p className="text-muted-foreground">Activity is restricted to administrators.</p>
        </div>
      </Layout>
    );
  }

  const hasFilters =
    !!filters.q ||
    !!filters.actorEmail ||
    !!filters.entityType ||
    !!filters.entityId ||
    !!filters.category ||
    filters.actions.length > 0 ||
    !!filters.from ||
    !!filters.to;

  const toggleAction = (action: string) => {
    setFilters((f) => ({
      ...f,
      actions: f.actions.includes(action)
        ? f.actions.filter((a) => a !== action)
        : [...f.actions, action],
    }));
  };

  const selectCategory = (cat: ActivityCategory | "") => {
    setFilters((f) => ({
      ...f,
      category: cat,
      // Drop any selected actions that fall outside the new category.
      actions: cat ? f.actions.filter((a) => actionInfo(a).category === cat) : f.actions,
    }));
  };

  return (
    <Layout>
      <div className="flex-1 overflow-auto">
        <div className="p-3 sm:p-6 space-y-5">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Activity</h1>
            <p className="text-xs text-muted-foreground">
              Every recorded action across accounts, inventory, sales, and banking.
            </p>
          </div>

          {/* Full-width workspace card: condensed filter strip + table */}
          <div className="border border-border rounded-md bg-card overflow-hidden flex flex-col">
            {/* Filter strip */}
            <div className="p-2 border-b border-border bg-panel flex flex-wrap gap-2 items-center shrink-0">
              {/* Category chips */}
              <ToggleChip active={!filters.category} onClick={() => selectCategory("")}>
                All
              </ToggleChip>
              {CATEGORY_CHIPS.map((cat) => (
                <ToggleChip
                  key={cat}
                  active={filters.category === cat}
                  onClick={() => selectCategory(filters.category === cat ? "" : cat)}
                >
                  {CATEGORY_LABEL[cat]}
                </ToggleChip>
              ))}

              <span className="mx-0.5 h-5 w-px bg-border" aria-hidden />

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search…"
                  value={filters.q}
                  onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                  className="h-7 text-xs w-56 pl-7"
                />
              </div>

              {/* Actor email */}
              {actorOptions.length > 0 ? (
                <Select
                  value={filters.actorEmail || "all"}
                  onValueChange={(v) =>
                    setFilters((f) => ({ ...f, actorEmail: v === "all" ? "" : v }))
                  }
                >
                  <SelectTrigger className="h-7 text-xs w-auto min-w-[140px] bg-card" aria-label="Actor">
                    <SelectValue placeholder="All actors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">All actors</SelectItem>
                    {actorOptions.map((a) => (
                      <SelectItem key={a} value={a} className="text-xs">{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="Actor email"
                  value={filters.actorEmail}
                  onChange={(e) => setFilters((f) => ({ ...f, actorEmail: e.target.value }))}
                  className="h-7 text-xs w-40"
                />
              )}

              {/* Entity type */}
              <Select
                value={filters.entityType || "all"}
                onValueChange={(v) =>
                  setFilters((f) => ({ ...f, entityType: v === "all" ? "" : v }))
                }
              >
                <SelectTrigger className="h-7 text-xs w-auto min-w-[130px] bg-card" aria-label="Entity type">
                  <SelectValue placeholder="Entity type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">All entity types</SelectItem>
                  {ENTITY_TYPES.map((t) => (
                    <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Entity ID */}
              <div className="relative">
                <Hash className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Entity ID"
                  value={filters.entityId}
                  onChange={(e) => setFilters((f) => ({ ...f, entityId: e.target.value }))}
                  className="h-7 text-xs w-32 pl-7"
                />
              </div>

              {/* Actions multi-select */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={`h-7 inline-flex items-center gap-1.5 rounded-md border px-2.5 text-xs transition-colors ${
                      filters.actions.length
                        ? "bg-primary/15 text-primary border-primary/40"
                        : "bg-card border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <ListFilter className="h-3.5 w-3.5 shrink-0" />
                    <span>Actions</span>
                    {filters.actions.length > 0 && (
                      <Badge
                        variant="secondary"
                        className="h-4 px-1 text-[10px] tabular-nums"
                      >
                        {filters.actions.length}
                      </Badge>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-72 p-0 max-h-80 overflow-auto">
                  <div className="p-2 space-y-2">
                    {visibleActionGroups.map(([cat, actions]) => (
                      <div key={cat}>
                        <div className="px-1 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                          {CATEGORY_LABEL[cat]}
                        </div>
                        {actions.map((a) => (
                          <label
                            key={a}
                            className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted cursor-pointer text-xs"
                          >
                            <Checkbox
                              checked={filters.actions.includes(a)}
                              onCheckedChange={() => toggleAction(a)}
                            />
                            <span>{actionInfo(a).label}</span>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Date range */}
              <DateChip
                placeholder="From"
                value={filters.from}
                onChange={(v) => setFilters((f) => ({ ...f, from: v }))}
              />
              <DateChip
                placeholder="To"
                value={filters.to}
                onChange={(v) => setFilters((f) => ({ ...f, to: v }))}
              />

              {hasFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs px-2 ml-auto text-muted-foreground hover:text-foreground"
                  onClick={() => setFilters(EMPTY)}
                >
                  <X className="h-3.5 w-3.5 mr-1" /> Clear filters
                </Button>
              )}
            </div>

            {/* Table */}
            <div className="p-3">
              <ActivityTable
                entries={items}
                isLoading={isLoading}
                total={total}
                canLoadMore={items.length < total}
                loadingMore={isFetching && !isLoading}
                onLoadMore={() => setPageCount((c) => c + 1)}
              />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
